/**
 * 波形表示用のピーク列 (振幅の最大値を等間隔に間引いたもの) と、その描画高さの式。
 * ナレーション分割位置の調整 (scenario/narrationSplit.ts) と動画エディタの波形トリム (WaveformTrimmer) が
 * 同じ式で描く。BE の voice_lines (peaks) もこの形 (0〜1 の振幅ピーク) で返す。
 */

/** 波形の高さの下限 (dBFS)。これより小さい音は高さ 0 (無音) として描く */
export const WAVEFORM_FLOOR_DB = -54;

/**
 * 振幅ピーク (0〜1) を波形の高さの比率 (0〜1) にする。
 * 振幅のまま描くと「す」の /s/ のような小さな子音 (ピーク 0.01 前後) が 1px にもならず、
 * 無音に見える境界が実は子音に食い込んでいることに気づけない。dB で描いて小さい音も見えるようにする。
 */
export function waveHeightRatio(peak: number): number {
  if (!(peak > 0)) return 0;
  const db = 20 * Math.log10(Math.min(1, peak));
  return Math.max(0, Math.min(1, (db - WAVEFORM_FLOOR_DB) / -WAVEFORM_FLOOR_DB));
}

/**
 * デコード済み音声から count 個のピーク (各区間の全チャンネルの振幅最大値・0〜1) を作る。
 * 区間はサンプル数を等分する。音声が短くて区間にサンプルが無ければ 0。
 */
export function computePeaks(buffer: AudioBuffer, count: number): Float32Array {
  const n = Math.max(1, Math.floor(count));
  const peaks = new Float32Array(n);
  const length = buffer.length;
  if (length <= 0) return peaks;
  const channels: Float32Array[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) channels.push(buffer.getChannelData(c));
  for (let i = 0; i < n; i++) {
    const from = Math.floor((i * length) / n);
    const to = Math.max(from + 1, Math.floor(((i + 1) * length) / n));
    let peak = 0;
    for (const data of channels) {
      for (let s = from; s < to && s < length; s++) {
        const v = Math.abs(data[s]!);
        if (v > peak) peak = v;
      }
    }
    peaks[i] = Math.min(1, peak);
  }
  return peaks;
}

/** 描画幅 (px) ごとに、その px が受け持つピークの最大値を返す (ピーク列が幅より細かくても粗くてもよい)。 */
export function peakAtPixel(peaks: ArrayLike<number>, x: number, width: number): number {
  if (peaks.length === 0 || width <= 0) return 0;
  const perPx = peaks.length / width;
  const from = Math.floor(x * perPx);
  const to = Math.max(from + 1, Math.floor((x + 1) * perPx));
  let peak = 0;
  for (let i = from; i < to && i < peaks.length; i++) if (peaks[i]! > peak) peak = peaks[i]!;
  return peak;
}
